import { useMutation, UseMutationResult } from "@tanstack/react-query";

import {
  ApiResponse,
  SupportApiResponse,
  getOscConfig,
} from "@twiliointernal/e2-commons-shared-definitions";
import { handleResponse } from "@twiliointernal/e2-commons-shared-utils";

import {
  CreateSendGridTicketData,
  UseCallbackRequestArgs,
  UseCallbackRequestProps,
} from "./definitions";
import { composeFormData } from "./utils";

const { supportAuthorizerEndpoint } = getOscConfig();

/**
 * IMPORTANT NOTES
 *  - This component is explicity built to be used for SendGrid ingress
 *  - This doesn't use the WiseOwl/HelpCenter Clients b/c it's making a request to SupportApi
 *  - This also doesn't use the existing SupportApi Client b/c it needs to go through support-authorizer first
 *  - TODO: Clean up to make the component re-usable for any ingress
 */
const useCallbackRequest = ({
  token,
}: UseCallbackRequestProps): UseMutationResult<
  SupportApiResponse<CreateSendGridTicketData>,
  any,
  UseCallbackRequestArgs
> => {
  return useMutation({
    mutationFn: async ({ data, options }: UseCallbackRequestArgs) => {
      // Only current use case is for SG ingress.
      const { ticketType } = options;

      const ticketCreationPath = ticketType
        ? `/tickets?brand=SendGrid&type=${ticketType}`
        : "/tickets?brand=SendGrid";

      const requestHeaders = new Headers();
      requestHeaders.append("i-sendgrid-auth-mako-token", token || "");
      requestHeaders.append("i-sendgrid-auth-user-id", data.userId);

      const formDataToSubmit: FormData = composeFormData(data);

      // See notes above on why we are not relying on existing client for this use case
      const fetched = await fetch(
        `${supportAuthorizerEndpoint}${ticketCreationPath}`,
        {
          method: "POST",
          headers: requestHeaders,
          body: formDataToSubmit,
        }
      );

      const response = (await handleResponse(
        fetched
      )) as ApiResponse<CreateSendGridTicketData>;

      return response as SupportApiResponse<CreateSendGridTicketData>;
    },
  });
};

export default useCallbackRequest;
